import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { ArrowLeft, Download, Edit, Eye, FileText, User, Package, Calendar, DollarSign, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, AlertTriangle, Shield } from '../components/Icons';
import { apiGet } from '../utils/api';
import { downloadInvoicePdf } from '../utils/downloadInvoicePdf';
import { getFbrStatusColor, getFbrStatusIcon } from '../utils/fbrStatus';
import { toast } from 'react-hot-toast';

const InvoiceView = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await apiGet(`/api/invoices/${id}`);
      if (response.ok) {
        console.log('Invoice API response:', response.data);
        setInvoice(response.data.invoice);
      } else {
        setError('Failed to fetch invoice');
        toast.error('Failed to fetch invoice');
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError('Error fetching invoice');
      toast.error('Error fetching invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      await downloadInvoicePdf(id);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(error.message || 'Error downloading PDF');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  const toggleItemExpansion = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  if (loading) {
    return (
      <Navigation>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading invoice...</p>
          </div>
        </div>
      </Navigation>
    );
  }

  if (error || !invoice) {
    return (
      <Navigation>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">{error || 'Invoice not found'}</p>
            <Link to="/invoices" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
              Back to Invoices
            </Link>
          </div>
        </div>
      </Navigation>
    );
  }

  return (
    <Navigation>
      <div className="min-h-screen bg-slate-50 py-8">
        {/* Action Buttons Bar at the Top */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center">
            <Link to="/invoices" className="mr-4 text-slate-600 hover:text-slate-900 bg-white p-2 rounded-full shadow border border-slate-200 flex items-center justify-center transition-all duration-200 hover:scale-105">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <span className="text-2xl font-bold text-slate-800 tracking-tight">Invoice Details</span>
              <p className="text-xs text-slate-500 mt-0.5">Reference: {invoice.invoice_number}</p>
            </div>
          </div>
          <div className="flex space-x-3 w-full sm:w-auto">
            <Link
              to={`/invoices/${id}/edit`}
              className="flex-1 sm:flex-none justify-center bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 flex items-center shadow-sm text-sm font-semibold transition-all duration-200 hover:shadow"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Invoice
            </Link>
            <button
              onClick={handleDownloadPdf}
              className="flex-1 sm:flex-none justify-center bg-emerald-600 text-white px-4 py-2.5 rounded-lg hover:bg-emerald-700 flex items-center shadow-sm text-sm font-semibold transition-all duration-200 hover:shadow"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </button>
          </div>
        </div>

        {/* Main layout grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main Invoice Sheet (Left Side) */}
          <div className="lg:col-span-2 space-y-6">

            {/* Invoice Header Details */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8">
              {/* Header Info */}
              <div className="flex flex-col sm:flex-row justify-between items-start mb-6 pb-6 border-b border-slate-100">
                <div className="flex items-start mb-4 sm:mb-0 gap-4">
                  {(!logoError && (invoice.tenant?.id || invoice.tenant_id)) ? (
                    <div className="w-16 h-12 bg-white border border-slate-200 rounded-lg overflow-hidden flex items-center justify-center shadow-inner">
                      <img
                        src={`${process.env.REACT_APP_API_URL || ''}/api/tenant/logo-file/${invoice.tenant?.id || invoice.tenant_id}`}
                        alt={invoice.tenant?.name || 'Seller Logo'}
                        className="max-w-full max-h-full object-contain p-0.5"
                        onError={() => setLogoError(true)}
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-12 bg-slate-100 border border-slate-200 text-slate-500 flex items-center justify-center font-extrabold text-sm rounded-lg uppercase tracking-wider shadow-inner">
                      {invoice.tenant?.name ? invoice.tenant.name.substring(0, 2).toUpperCase() : 'POS'}
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{invoice.tenant?.name || 'Seller Business'}</h2>
                    <div className="text-[12px] text-slate-500 mt-1 space-y-0.5">
                      <p>{invoice.tenant?.seller_address || 'Address N/A'}</p>
                      <p>
                        NTN: {invoice.tenant?.seller_ntn_cnic || 'N/A'}
                        {invoice.tenant?.seller_strn && ` | STRN: ${invoice.tenant.seller_strn}`}
                        {invoice.tenant?.seller_province && ` | ${invoice.tenant.seller_province}`}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end w-full sm:w-auto">
                  <h1 className="text-xl font-extrabold text-slate-900 uppercase tracking-wider">Sales Tax Invoice</h1>
                  <span className={`mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(invoice.status)} shadow-sm`}>
                    Payment: {invoice.status?.toUpperCase() || 'PENDING'}
                  </span>
                </div>
              </div>

              {/* Buyer & Seller Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-slate-600">
                {/* Buyer Details */}
                <div className="bg-slate-50/50 rounded-lg p-4 border border-slate-100">
                  <h3 className="font-bold text-slate-800 mb-3 text-xs uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2">
                    <User className="h-4 w-4 text-indigo-500" /> Buyer Details
                  </h3>
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="w-24 text-slate-400 font-medium">Name:</span>
                      <span className="font-semibold text-slate-800">{invoice.client?.name || 'N/A'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 text-slate-400 font-medium">NTN/CNIC:</span>
                      <span className="font-semibold text-slate-800">{invoice.client?.ntn_cnic || 'N/A'}</span>
                    </div>
                    {invoice.client?.buyer_strn && (
                      <div className="flex">
                        <span className="w-24 text-slate-400 font-medium">STRN:</span>
                        <span className="font-semibold text-slate-800">{invoice.client.buyer_strn}</span>
                      </div>
                    )}
                    <div className="flex">
                      <span className="w-24 text-slate-400 font-medium">Address:</span>
                      <span className="font-semibold text-slate-800 flex-1">{invoice.client?.address || 'N/A'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 text-slate-400 font-medium">Province:</span>
                      <span className="font-semibold text-slate-800">{invoice.client?.province || 'N/A'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-24 text-slate-400 font-medium">Registration:</span>
                      <span className="font-semibold text-slate-800">{invoice.client?.buyer_registration_type || 'Registered'}</span>
                    </div>
                  </div>
                </div>

                {/* Seller/Invoice General Details */}
                <div className="bg-slate-50/50 rounded-lg p-4 border border-slate-100">
                  <h3 className="font-bold text-slate-800 mb-3 text-xs uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-2">
                    <FileText className="h-4 w-4 text-indigo-500" /> Details & Reference
                  </h3>
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="w-28 text-slate-400 font-medium">Reference No:</span>
                      <span className="font-semibold text-slate-800">{invoice.invoice_number}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 text-slate-400 font-medium">Invoice Date:</span>
                      <span className="font-semibold text-slate-800">
                        {invoice.invoice_date
                          ? new Date(invoice.invoice_date).toLocaleDateString()
                          : (invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : '—')}
                      </span>
                    </div>
                    <div className="flex">
                      <span className="w-28 text-slate-400 font-medium">Invoice Type:</span>
                      <span className="font-semibold text-slate-800">{invoice.invoice_type || 'Sale Invoice'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 text-slate-400 font-medium">Tenant POS ID:</span>
                      <span className="font-semibold text-slate-800">{invoice.tenant?.pos_id || 'N/A'}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 text-slate-400 font-medium">Created On:</span>
                      <span className="font-semibold text-slate-800">
                        {invoice.created_at ? new Date(invoice.created_at).toLocaleString() : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-bold text-slate-800 mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
                <Package className="h-5 w-5 text-indigo-500" /> Invoice Line Items
              </h3>

              <div className="overflow-x-auto rounded-lg border border-slate-100">
                <table className="min-w-full divide-y divide-slate-200 text-xs">
                  <thead className="bg-slate-50 text-slate-700 font-bold uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="px-3 py-3 text-center w-10">Sr#</th>
                      <th className="px-3 py-3 text-left">Product Details</th>
                      <th className="px-3 py-3 text-center w-16">Qty</th>
                      <th className="px-3 py-3 text-right w-24">Unit Price</th>
                      <th className="px-3 py-3 text-right w-24">Retail Price</th>
                      <th className="px-3 py-3 text-right w-28">Value Exc. Tax</th>
                      <th className="px-3 py-3 text-right w-20">Tax Value</th>
                      <th className="px-3 py-3 text-right w-28">Value Incl. Tax</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100 text-slate-600">
                    {invoice.items && invoice.items.length > 0 ? (
                      invoice.items.map((item, index) => {
                        const additionalInfo = [];
                        if (item.uom) additionalInfo.push(item.uom);
                        if (item.hs_code) additionalInfo.push(item.hs_code);
                        if (item.sale_type) additionalInfo.push(item.sale_type);
                        if (item.sro_schedule_no) additionalInfo.push(item.sro_schedule_no);
                        if (item.sro_item_serial_no) additionalInfo.push(item.sro_item_serial_no);

                        const tr = item.tax_percentage;
                        const taxRateStr = tr ? `${parseFloat(tr)}%` : '0%';
                        const additionalText = additionalInfo.join(', ');

                        // Format quantity dynamically (up to 2 decimals)
                        const qtyVal = Number(item.quantity || 0);
                        const qtyStr = qtyVal % 1 === 0 ? qtyVal.toFixed(0) : qtyVal.toFixed(2);

                        // Format unit price dynamically (up to 2 decimals)
                        const priceVal = Number(item.unit_price || 0);
                        const priceStr = priceVal % 1 === 0 ? priceVal.toFixed(0) : priceVal.toFixed(2);

                        return (
                          <tr key={index} className="hover:bg-slate-50/50 transition-colors duration-150">
                            <td className="px-3 py-3 text-center font-semibold text-slate-400">{index + 1}</td>
                            <td className="px-3 py-3">
                              <div className="font-bold text-slate-800 text-[12px]">{item.product_name}</div>
                              {item.product_description && (
                                <div className="text-slate-500 text-[10px] mt-0.5">{item.product_description}</div>
                              )}
                              <div className="text-slate-600 text-[10px] mt-1 flex items-center gap-1.5">
                                <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-semibold text-[9px]">Tax: {taxRateStr}</span>
                                {additionalText && (
                                  <span className="text-[9px] text-slate-400 font-mono">({additionalText})</span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-center font-mono font-semibold text-slate-800">{qtyStr}</td>
                            <td className="px-3 py-3 text-right font-mono text-slate-700">{priceStr}</td>
                            <td className="px-3 py-3 text-right font-mono text-slate-700">
                              {Number(item.fixed_notified_value || item.retail_price || item.unit_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-slate-700">
                              {Number(item.value_sales_excluding_st || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-slate-700">
                              {Number(item.tax_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-3 text-right font-mono font-bold text-slate-900">
                              {Number(item.total_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-6 py-8 text-center text-slate-400">
                          No items found for this invoice.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {/* Totals Table Footer Row */}
                  <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-bold text-slate-800">
                    <tr>
                      <td colSpan="5" className="px-3 py-3 text-left tracking-wider text-[10px]">TOTAL</td>
                      <td className="px-3 py-3 text-right font-mono">
                        {Number(invoice.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-3 text-right font-mono">
                        {Number(invoice.tax_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-slate-900">
                        {Number(invoice.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Note Section Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-bold text-slate-800 mb-3 text-xs uppercase tracking-wider">Terms & Notes</h3>
              <p className="text-[11px] text-slate-500 leading-relaxed font-semibold">Note: This is a system-generated sales tax invoice and does not require any signature or stamp.</p>
              {invoice.notes && (
                <div className="mt-3 text-xs text-slate-600 p-4 bg-slate-50 rounded-lg border border-slate-200/60">
                  <span className="font-bold block text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Additional Notes:</span>
                  <p className="whitespace-pre-line leading-relaxed">{invoice.notes}</p>
                </div>
              )}
            </div>

          </div>

          {/* Right Sidebar Column */}
          <div className="lg:col-span-1 space-y-6">

            {/* FBR Compliance Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-950 text-white px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-indigo-400" />
                  <span className="font-bold text-sm uppercase tracking-wider">FBR Compliance</span>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getFbrStatusColor(invoice.fbr_status)}`}>
                  {getFbrStatusIcon(invoice.fbr_status)}
                  <span className="ml-1 uppercase text-[10px]">{invoice.fbr_status?.replace('_', ' ') || 'Not Submitted'}</span>
                </span>
              </div>

              <div className="p-5 space-y-4">
                {/* Meta details */}
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="block text-slate-400 font-medium">POS ID</span>
                    <span className="font-mono font-bold text-slate-800">{invoice.pos_id || 'POS-FBR-001'}</span>
                  </div>
                  <div>
                    <span className="block text-slate-400 font-medium">FBR Invoice ID</span>
                    <span className="font-mono font-bold text-slate-800 break-all">
                      {/* Make a validation only id unmistakable, so it is never read as an IRN. */}
                      {/^FBR-\d{8}-[A-Z0-9]{5}$/.test(invoice.fbr_invoice_id || '')
                        ? `Mock-FBR-Validation-Id: ${invoice.fbr_invoice_id}`
                        : (invoice.fbr_invoice_id || 'N/A')}
                    </span>
                  </div>
                </div>

                {/* Validation/Submission Error Banner (If present) */}
                {invoice.fbr_error && (
                  <div className="bg-red-50/80 border border-red-200 p-4 rounded-lg text-xs">
                    <div className="flex gap-2 text-red-800 font-bold mb-1.5">
                      <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
                      <span>FBR Validation Failure</span>
                    </div>
                    <p className="text-red-700 leading-relaxed font-mono whitespace-pre-wrap select-all">
                      {invoice.fbr_error}
                    </p>
                  </div>
                )}

                {/* Raw FBR Response Collapsible logs */}
                {invoice.fbr_response && (
                  <div className="pt-2 border-t border-slate-100">
                    <details className="group cursor-pointer">
                      <summary className="list-none flex items-center justify-between text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors">
                        <span>FBR API RESPONSE LOGS</span>
                        <span className="transition-transform group-open:rotate-180 text-[10px]">▼</span>
                      </summary>
                      <div className="mt-3 relative">
                        <pre className="p-3 bg-slate-900 text-slate-100 rounded-lg overflow-x-auto text-left font-mono text-[10px] leading-relaxed max-h-80 overflow-y-auto whitespace-pre-wrap shadow-inner select-all">
                          {invoice.fbr_response}
                        </pre>
                      </div>
                    </details>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Summary Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-200 px-5 py-4">
                <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider flex items-center gap-1.5">
                  <DollarSign className="h-4 w-4 text-indigo-500" /> Invoice Summary
                </h3>
              </div>

              <div className="divide-y divide-slate-100 font-mono text-xs">
                <div className="flex justify-between px-5 py-3 text-slate-600">
                  <span className="text-slate-400">Exclusive Sales Value:</span>
                  <span className="font-bold text-slate-800">Rs. {parseFloat(invoice.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                <div className="flex justify-between px-5 py-3 text-slate-600">
                  <span className="text-slate-400">FED Payable:</span>
                  <span className="font-semibold text-slate-800">
                    Rs. {parseFloat(invoice.items?.reduce((sum, it) => sum + (Number(it.fed_payable) || 0), 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="flex justify-between px-5 py-3 text-slate-600">
                  <span className="text-slate-400">Further Tax:</span>
                  <span className="font-semibold text-slate-800">
                    Rs. {parseFloat(invoice.items?.reduce((sum, it) => sum + (Number(it.further_tax) || 0), 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="flex justify-between px-5 py-3 text-slate-600">
                  <span className="text-slate-400">Extra Tax:</span>
                  <span className="font-semibold text-slate-800">
                    Rs. {parseFloat(invoice.items?.reduce((sum, it) => sum + (Number(it.extra_tax) || 0), 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="flex justify-between px-5 py-3 text-slate-600">
                  <span className="text-slate-400">Withheld Sales Tax:</span>
                  <span className="font-semibold text-slate-800">
                    Rs. {parseFloat(invoice.items?.reduce((sum, it) => sum + (Number(it.sales_tax_withheld) || 0), 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                <div className="flex justify-between px-5 py-3 text-slate-600">
                  <span className="text-slate-400">Total Tax Amount:</span>
                  <span className="font-bold text-slate-800">Rs. {parseFloat(invoice.tax_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                <div className="flex justify-between px-5 py-3 text-slate-600">
                  <span className="text-slate-400">Discount Amount:</span>
                  <span className="font-semibold text-slate-800">Rs. {parseFloat(invoice.discount_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                {/* Grand Total Highlight */}
                <div className="bg-indigo-50/50 px-5 py-4 flex flex-col gap-1">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-900 font-bold text-xs uppercase tracking-wider">Total Payable (ST Incl.)</span>
                    <span className="font-extrabold text-indigo-700 text-lg">
                      Rs. {parseFloat(invoice.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </Navigation>
  );
};

export default InvoiceView;
